import { useEffect, useRef } from "react";

function MaximetroChart({ data, deviceName }) {
  const chartRef = useRef(null);

  const pickTs = (s) => s.timestamp_utc || s.timestamp || s.bucket_start;

  useEffect(() => {
    if (!chartRef.current || !data) return;

    const echarts = require("echarts");
    const inst = echarts.init(chartRef.current);

    const seriesRows = Array.isArray(data.series)
      ? data.series
          .map((s) => ({
            ts: pickTs(s),
            demand: Number(s.measured_demand ?? s.raw_demand ?? 0),
            raw: Number(s.raw_demand ?? 0),
            energy: Number(s.interval_energy ?? 0),
            contractedPower: Number(
              s.contracted_power ??
                data?.contracted_power ??
                data?.result?.overall?.contracted_power ??
                0,
            ),
          }))
          .filter((r) => !Number.isNaN(new Date(r.ts).getTime()))
      : [];

    const points = seriesRows.map((r) => [new Date(r.ts).getTime(), r.demand]);

    const contractedPowerPoints = seriesRows
      .filter(
        (r) => Number.isFinite(r.contractedPower) && r.contractedPower > 0,
      )
      .map((r) => [new Date(r.ts).getTime(), r.contractedPower]);

    const overall = data.result?.overall;
    const byTou = data.result?.by_tou;
    const billableBands = data.config?.billable_band_codes || [];
    const contractedPower = Number(
      data?.contracted_power ??
        data?.result?.overall?.contracted_power ??
        data?.config?.contracted_power ??
        data?.config?.potencia_contratada ??
        0,
    );

    const colors = {
      PUNTA: "#e74c3c",
      LLANO: "#f39c12",
      VALLE: "#3498db",
      GENERAL: "#2c3e50",
      CONTRACTED: "#8e44ad",
    };

    let referenceLines = [];
    let peakPoints = [];

    if (billableBands.length > 0 && byTou) {
      billableBands.forEach((code) => {
        const band = byTou[code];
        if (!band) return;

        const val = Number(band.measured_max_demand ?? band.raw_max_demand);
        const ts = band.timestamp_of_max_utc;

        if (!Number.isFinite(val) || !ts) return;

        referenceLines.push({
          yAxis: val,
          lineStyle: {
            color: colors[code] || "#999",
            type: "dashed",
            width: 1.5,
          },
          label: { show: false },
        });

        peakPoints.push({
          coord: [new Date(ts).getTime(), val],
          name: code,
          itemStyle: { color: colors[code] || "#999" },
        });
      });
    }

    if (
      referenceLines.length === 0 &&
      overall
    ) {
      const val = Number(overall.measured_max_demand ?? overall.raw_max_demand);
      const ts = overall.timestamp_of_max_utc;

      if (Number.isFinite(val) && ts) {
        referenceLines.push({
          yAxis: val,
          lineStyle: {
            color: colors.GENERAL,
            type: "dashed",
            width: 1.5,
          },
          label: { show: false },
        });

        peakPoints.push({
          coord: [new Date(ts).getTime(), val],
          name: "GENERAL",
          itemStyle: { color: colors.GENERAL },
        });
      }
    }

    const option = {
      tooltip: {
        trigger: "axis",
        formatter: function (params) {
          if (!params || !params.length) return "";

          const demandName = deviceName || "Demanda";
          const demandPoint =
            params.find((p) => p.seriesName === demandName) || params[0];

          const date = new Date(demandPoint.value[0]);
          const demand = demandPoint.value[1];

          const idx = points.findIndex((pt) => pt[0] === demandPoint.value[0]);
          const energy = idx >= 0 ? seriesRows[idx].energy : null;
          const cp = idx >= 0 ? seriesRows[idx].contractedPower : contractedPower;

          return `
            ${new Intl.DateTimeFormat("es-AR", {
              timeZone: "America/Argentina/Buenos_Aires",
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hourCycle: "h23",
            }).format(date)}
            <br/>Demanda: ${Number(demand)} kW
            ${energy != null ? `<br/>Energía: ${Number(energy).toFixed(3)} kWh` : ""}
            ${
              Number.isFinite(cp) && cp > 0
                ? `<br/>Contratada: ${Number(cp)} kW`
                : ""
            }
          `;
        },
      },
      legend: {
        show: true,
        data: [
          deviceName || "Demanda",
          ...(Number.isFinite(contractedPower) && contractedPower > 0
            ? ["Contratada"]
            : []),
        ],
      },
      xAxis: {
        type: "time",
        axisLabel: {
          formatter: (val) =>
            new Intl.DateTimeFormat("es-AR", {
              timeZone: "America/Argentina/Buenos_Aires",
              hour: "2-digit",
              minute: "2-digit",
              hourCycle: "h23",
            }).format(new Date(val)),
        },
      },
      yAxis: { type: "value", name: "kW" },
      series: [
        {
          name: deviceName || "Demanda",
          type: "line",
          data: points,
          smooth: true,
          connectNulls: true,
          lineStyle: { width: 2 },
          markLine:
            referenceLines.length > 0
              ? {
                  symbol: ["none", "none"],
                  label: {
                    show: false,
                  },
                  data: referenceLines,
                }
              : undefined,
          markPoint:
            peakPoints.length > 0
              ? {
                  symbolSize: 45,
                  data: peakPoints,
                  label: {
                    show: false,
                  },
                }
              : undefined,
        },
        ...(Number.isFinite(contractedPower) && contractedPower > 0
          ? [
              {
                name: "Contratada",
                type: "line",
                data: contractedPowerPoints,
                smooth: false,
                connectNulls: true,
                showSymbol: false,
                silent: true,
                lineStyle: {
                  width: 2,
                  type: "solid",
                  color: colors.CONTRACTED,
                },
                emphasis: {
                  disabled: true,
                },
              },
            ]
          : []),
      ],
    };

    inst.setOption(option);
    const handleResize = () => inst.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      inst.dispose();
    };
  }, [data, deviceName]);

  return (
    <div style={{ position: "relative" }}>
      {data?.quality?.status === "INVALID" && (
        <div style={{ position: "absolute", right: 8, top: 8, zIndex: 10 }}>
          <span
            style={{
              background: "#e74c3c",
              color: "#fff",
              padding: "4px 8px",
              borderRadius: 4,
            }}
          >
            Calidad: INVALID
          </span>
        </div>
      )}
      <div ref={chartRef} style={{ width: "100%", height: 360 }} />
    </div>
  );
}

export default MaximetroChart;